import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import ProductForm from './ProductForm';
import ProductItem from './productitem';
import { FiSearch, FiPlus, FiAlertTriangle, FiUpload, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { inventoryApi } from '../api';

const Inventory =({ currency, setCurrency }) => {
  const [products, setProducts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExpiryAlerts, setShowExpiryAlerts] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);


  const categories = ['All', 'Grains', 'Spices', 'Dairy', 'Vegetables', 'Meat'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const productsData = await inventoryApi.getMaterials(activeCategory === 'All' ? null : activeCategory);
        const alertsData = await inventoryApi.getAlerts();
        setProducts(productsData);
        setAlerts(alertsData);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeCategory]);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'All' || product.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddProduct = async (formData) => {
    try {
      setIsSubmitting(true);
      const createdProduct = await inventoryApi.createMaterial(formData);
      setProducts(prev => [...prev, createdProduct]);
      setShowForm(false);
    } catch (err) {
      console.error('Error adding product:', err);
      alert(`Error adding product: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProduct = async (formData) => {
    try {
      if (!editingProduct) return;
      setIsSubmitting(true);
      
      const updatedProduct = await inventoryApi.updateMaterial(
        editingProduct.id, 
        formData,
        formData.get('remove_image') === 'true'
      );
      
      setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
      setEditingProduct(null);
      setShowForm(false);
    } catch (err) {
      console.error('Error updating product:', err);
      alert(`Error updating product: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this material?')) {
      try {
        await inventoryApi.deleteMaterial(id);
        setProducts(prev => prev.filter(p => p.id !== id));
      } catch (err) {
        console.error('Error deleting product:', err);
        alert(`Error deleting product: ${err.message}`);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileInputKey(Date.now());
  };

 const handleExcelUpload = async (e) => {
  if (e.target.files.length > 0) {
    const file = e.target.files[0];
    setSelectedFile(file);
    setUploadProgress(0);

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await inventoryApi.uploadExcelMaterials(formData, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      });

      // Refresh the inventory after successful upload
      const productsData = await inventoryApi.getMaterials(activeCategory === 'All' ? null : activeCategory);
      setProducts(productsData);
      
      alert(`Successfully imported ${response.data.length} materials`);

    } catch (err) {
      console.error('Upload Error:', err);
      let errorMessage = 'Error uploading Excel file';
      
      if (err.response) {
        if (err.response.status === 501) {
          errorMessage = 'Server missing required Excel processing dependencies';
        } else if (err.response.data?.detail) {
          errorMessage = err.response.data.detail;
        }
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
      setSelectedFile(null);
      setFileInputKey(Date.now());
      setUploadProgress(0);
    }
  }
};

  return (
    <Layout currency={currency} setCurrency={setCurrency}>
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="relative w-full md:w-1/3">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-2 items-center">
          <div className="flex items-center gap-2">
            <input
              key={fileInputKey}
              type="file"
              id="excel-upload"
              accept=".xlsx, .xls"
              className="hidden"
              onChange={handleExcelUpload}
            />
            <label
              htmlFor="excel-upload"
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              <FiUpload /> Upload Excel
            </label>
            
            {selectedFile && (
              <div className="flex flex-col">
                <div className="flex items-center bg-gray-100 rounded-lg px-3 py-1">
                  <span className="text-sm font-medium truncate max-w-[120px]">
                    {selectedFile.name}
                  </span>
                  <button
                    onClick={handleRemoveFile}
                    className="ml-2 text-gray-500 hover:text-gray-700"
                    aria-label="Remove file"
                  >
                    <FiX size={16} />
                  </button>
                </div>
                {uploadProgress > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                    <div 
                      className="bg-green-600 h-1.5 rounded-full" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => {
              setEditingProduct(null);
              setShowForm(true);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <FiPlus /> Add Raw Material
          </button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowExpiryAlerts(!showExpiryAlerts)}
            className="w-full flex items-center justify-between p-4 bg-yellow-50 hover:bg-yellow-100 transition-colors"
          >
            <div className="flex items-center">
              <FiAlertTriangle className="text-yellow-500 mr-2" size={20} />
              <h3 className="text-lg font-medium text-yellow-800">
                Inventory Alerts ({alerts.length})
              </h3>
            </div>
            {showExpiryAlerts ? (
              <FiChevronUp className="text-yellow-600" size={20} />
            ) : (
              <FiChevronDown className="text-yellow-600" size={20} />
            )}
          </button>
          
          {showExpiryAlerts && (
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="space-y-3">
                {alerts.map(alert => {
                  const expiryDate = alert.expiry_date ? new Date(alert.expiry_date) : null;
                  const today = new Date();
                  const daysUntilExpiry = expiryDate ? Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24)) : null;
                  
                  return (
                    <div key={alert.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-md">
                      <div>
                        <p className="font-medium text-yellow-800">{alert.name}</p>
                        <p className="text-sm text-yellow-700">
                          {alert.category} • {alert.current_quantity} {alert.unit}
                        </p>
                      </div>
                      <div className="text-right">
                        {alert.stock_message && (
                          <p className="text-sm font-medium text-yellow-800">
                            {alert.stock_message}
                          </p>
                        )}
                        {alert.expiry_message && (
                          <p className="text-sm text-yellow-700">
                            {alert.expiry_message}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-6">
        <div className="flex overflow-x-auto pb-2 gap-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-full whitespace-nowrap ${
                activeCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap -mx-1">
        {loading ? (
          <div className="w-full text-center py-8">Loading products...</div>
        ) : error ? (
          <div className="w-full text-center py-8 text-red-500">Error loading products: {error}</div>
        ) : filteredProducts.length > 0 ? (
          filteredProducts.map(product => (
            <ProductItem
              key={product.id}
              product={product}
              currency={currency}
              onEdit={() => {
                setEditingProduct(product);
                setShowForm(true);
              }}
              onDelete={() => handleDelete(product.id)}
              onReorder={() => {
                console.log('Reorder:', product.name);
              }}
            />
          ))
        ) : (
          <div className="w-full text-center py-8 text-gray-500">
            No products found. Try a different search or category.
          </div>
        )}
      </div>

      {showForm && (
        <ProductForm
          product={editingProduct}
          onClose={() => setShowForm(false)}
          onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}
          isSubmitting={isSubmitting}
          activeCategory={activeCategory === 'All' ? null : activeCategory}
          currency={currency}
        />
      )}
    </Layout>
  );
};

export default Inventory;